import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGoogle, FaArrowLeft, FaTimes, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import Navbar from '../components/Navbar';
import OTPInput from '../components/OTPInput';
import { getApiUrl, getApiUrlAbsolute } from '../config/api';
import { registerPushNotifications } from '../services/pushNotificationService';
import './LoginPage.css';

const COMPANY_HOST_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);

const getPostLoginRedirect = (user) => {
  const storedRedirect = localStorage.getItem('authRedirectPath');
  if (storedRedirect) {
    localStorage.removeItem('authRedirectPath');
    return storedRedirect;
  }

  const role = String(user?.role || '');
  const hasCompany = Boolean(user?.company?._id || user?.company);
  if (COMPANY_HOST_ROLES.has(role) || hasCompany) {
    return '/host';
  }

  return '/';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [step, setStep] = useState('login'); // 'login', 'otp', 'loading', 'success'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpError, setOtpError] = useState('');
  const [shakeAnimation, setShakeAnimation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [authMode, setAuthMode] = useState('otp'); // 'otp' | 'password'
  const [password, setPassword] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Email validation
  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) {
      setEmailError('');
      return false;
    }
    if (!emailRegex.test(emailValue)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
    setError('');
  };

  // Check for URL error parameters (Google callback, OTP, etc.)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const detail = urlParams.get('detail') || '';
    if (errorParam === 'unauthorized') {
      setError('Your email is not authorized to access this system. Please contact administrator.');
    } else if (errorParam === 'server_error') {
      setError('Server error occurred. Please try again.');
    } else if (errorParam === 'google_not_configured') {
      setError('Google sign-in is not configured. Please use email + OTP to log in.');
    } else if (errorParam === 'google_failed') {
      const isRedirectMismatch = detail === 'redirect_uri_mismatch' || (detail && detail.includes('redirect_uri'));
      setError(
        isRedirectMismatch
          ? 'Google sign-in failed: redirect URI mismatch. Add this exact URL in Google Cloud Console → APIs & Services → Credentials → your OAuth client → Authorized redirect URIs: http://localhost:5002/api/auth/google/callback (then Save and try again).'
          : `Google sign-in failed. ${detail ? decodeURIComponent(detail) : 'Please try again or use Email + OTP.'}`
      );
    } else if (errorParam === 'no_token') {
      setError('Login session expired or invalid. Please try again.');
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Redirect to Google OAuth (use full server URL so browser hits API, not React dev server)
      const redirectBase = encodeURIComponent(window.location.origin);
      const redirectPath = encodeURIComponent('/auth/callback');
      window.location.href = getApiUrlAbsolute(`/api/auth/google?redirect=${redirectBase}&redirectPath=${redirectPath}`);
    } catch (err) {
      setError('Failed to initiate Google Sign-In. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    if (!validateEmail(email)) return;
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(getApiUrl('/api/auth/login-password'), {
        email: email.trim(),
        password,
      });
      if (response.data.success) {
        setShowSuccess(true);
        setStep('success');
        const loggedInUser = response.data.user;
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        window.dispatchEvent(new CustomEvent('codex-authenticated'));
        await registerPushNotifications().catch(() => {});
        const redirectPath = getPostLoginRedirect(loggedInUser);
        setTimeout(() => {
          navigate(redirectPath);
          window.location.reload();
        }, 1500);
      } else {
        setError(response.data.message || 'Sign in failed');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Invalid email or password'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    // Validate email
    if (!validateEmail(email)) {
      return;
    }

    if (authMode === 'password') {
      await handlePasswordLogin(e);
      return;
    }

    // Use OTP flow
    setLoading(true);

    try {
      const response = await axios.post(
        getApiUrl('/api/auth/send-otp'),
        { email }
      );

      if (response.data.success) {
        setOtpSent(true);
        setStep('otp');
        setResendTimer(30);
        setError('');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('OTP Send Error:', err);
      
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('Cannot connect to server. Please make sure the backend server is running on port 5000.');
      } else if (err.response?.status === 403) {
        setError(err.response.data.message || 'This email is not authorized to access the system.');
      } else if (err.response?.status === 500) {
        setError(err.response.data.message || 'Server error. Please check MongoDB connection.');
      } else {
        setError(
          err.response?.data?.message || 
          'Failed to send OTP. Please try again or contact administrator.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (otpValue) => {
    setOtp(otpValue);
    setOtpError('');
  };

  const handleOtpComplete = async (otpValue) => {
    await handleOtpVerify(otpValue);
  };

  const handleOtpVerify = async (otpValue = otp) => {
    if (!otpValue || otpValue.length !== 6) {
      return;
    }

    setOtpError('');
    setLoading(true);
    setShakeAnimation(false);

    try {
      const response = await axios.post(
        getApiUrl('/api/auth/verify-otp'),
        { email, otp: otpValue }
      );

      if (response.data.success) {
        setShowSuccess(true);
        setStep('success');

        const loggedInUser = response.data.user;
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        window.dispatchEvent(new CustomEvent('codex-authenticated'));
        await registerPushNotifications().catch(() => {});
        const redirectPath = getPostLoginRedirect(loggedInUser);
        setTimeout(() => {
          navigate(redirectPath);
          window.location.reload();
        }, 1500);
      } else {
        setOtpError(response.data.message || 'Invalid OTP. Please try again.');
        setShakeAnimation(true);
        setTimeout(() => setShakeAnimation(false), 500);
      }
    } catch (err) {
      setOtpError(
        err.response?.data?.message || 
        'OTP verification failed. Please try again.'
      );
      setShakeAnimation(true);
      setTimeout(() => setShakeAnimation(false), 500);
    } finally {
      setLoading(false);
    }
  };

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setOtpError('');
    setOtp('');
    setLoading(true);

    try {
      const response = await axios.post(
        getApiUrl('/api/auth/resend-otp'),
        { email }
      );

      if (response.data.success) {
        setResendTimer(30);
        setOtpError('');
      } else {
        setOtpError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <Navbar isScrolled={isScrolled} />
      
      <div className="login-page">
        {/* Full-width luxury hotel background image */}
        <div className="login-background-image"></div>

        {/* Login Card - Floating on the right */}
        <div className="login-page-container">
          <motion.div
            className="login-card"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Top row: Back to Home and Close */}
            <div className="login-card-header">
              <Link to="/" className="back-home-link">
                <FaArrowLeft />
                <span>Back to Home</span>
              </Link>
              <button onClick={() => navigate('/')} className="close-button" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            {/* Title and Subtitle */}
            <div className="login-header">
              <h1 className="login-title">Welcome Back</h1>
              <p className="login-subtitle">
                {step === 'otp'
                  ? 'Enter the OTP sent to your email'
                  : authMode === 'password'
                    ? 'Company account: use email and password'
                    : 'Sign in to access your account'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
              </motion.div>
            )}

            {/* Login Form */}
            {step === 'login' && (
              <motion.div
                className="login-form-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <form onSubmit={handleEmailSubmit} className="login-form">
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={() => validateEmail(email)}
                      required
                      className={`form-input ${emailError ? 'input-error' : ''}`}
                      disabled={loading}
                    />
                    {emailError && (
                      <motion.div
                        className="field-error-message"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {emailError}
                      </motion.div>
                    )}
                  </div>

                  {authMode === 'password' && (
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(ev) => {
                          setPassword(ev.target.value);
                          setError('');
                        }}
                        autoComplete="current-password"
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode((m) => (m === 'otp' ? 'password' : 'otp'));
                      setOtpSent(false);
                      setOtp('');
                      setPassword('');
                      setError('');
                    }}
                    className="btn-change-email-link"
                    style={{ marginBottom: '0.75rem', display: 'block', width: '100%', textAlign: 'center' }}
                    disabled={loading}
                  >
                    {authMode === 'password' ? 'Use email code (OTP) instead' : 'Sign in with password (company account)'}
                  </button>

                  {/* OTP Input - shown after email is submitted */}
                  {authMode === 'otp' && otpSent && (
                    <>
                      <div className={`otp-input-wrapper ${shakeAnimation ? 'shake' : ''}`}>
                        <OTPInput
                          value={otp}
                          onChange={handleOtpChange}
                          onComplete={handleOtpComplete}
                          error={otpError}
                          disabled={loading}
                          length={6}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOtpVerify()}
                        className="btn-verify"
                        disabled={loading || otp.length !== 6}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-small"></span>
                            <span>Verifying...</span>
                          </>
                        ) : (
                          'Verify'
                        )}
                      </button>
                    </>
                  )}

                  {authMode === 'otp' && !otpSent && (
                    <button
                      type="submit"
                      className="btn-verify"
                      disabled={loading || !email || !!emailError}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-small"></span>
                          <span>Sending...</span>
                        </>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  )}

                  {authMode === 'password' && (
                    <button
                      type="submit"
                      className="btn-verify"
                      disabled={loading || !email || !!emailError || !password}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-small"></span>
                          <span>Signing in...</span>
                        </>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  )}
                </form>

                {authMode === 'otp' && !otpSent && (
                  <>
                    <div className="divider">
                      <span>or</span>
                    </div>

                    <button
                      onClick={handleGoogleSignIn}
                      className="btn-google"
                      disabled={loading}
                    >
                      <FaGoogle className="google-icon" />
                      <span>Continue with Google</span>
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {/* OTP Verification */}
            {step === 'otp' && (
              <motion.div
                className="otp-verification-container"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`otp-input-wrapper ${shakeAnimation ? 'shake' : ''}`}>
                  <OTPInput
                    value={otp}
                    onChange={handleOtpChange}
                    onComplete={handleOtpComplete}
                    error={otpError}
                    disabled={loading}
                    length={6}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleOtpVerify()}
                  className="btn-verify"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <>
                      <span className="spinner-small"></span>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>

                <div className="otp-secondary-actions">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="btn-resend-otp"
                    disabled={loading || resendTimer > 0}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('login');
                      setOtp('');
                      setOtpSent(false);
                      setOtpError('');
                      setResendTimer(0);
                    }}
                    className="btn-change-email-link"
                    disabled={loading}
                  >
                    Change Email
                  </button>
                </div>
              </motion.div>
            )}

            {/* Success State */}
            {step === 'success' && showSuccess && (
              <motion.div
                className="success-container"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: 'spring' }}
              >
                <FaCheckCircle className="success-icon-large" />
                <h2 className="success-title">Verification Successful!</h2>
                <p className="success-message">Redirecting you to the home page...</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
